import { EmeraldButton } from "@emerald-react/button";
import { EmeraldCheckbox } from "@emerald-react/checkbox";
import { EmeraldIconButton } from "@emerald-react/icon-button";
import { EmeraldNotification } from "@emerald-react/notification";
import { EmeraldProgressSpinner } from "@emerald-react/progress-indicator";
import useSurveyState from "@hooks/useSurveyState";
import {
  mediaUpload,
  useDeleteOwnerMediaFileMutation,
  useGetSurveyMediaFileQuery,
  useUploadOwnerMediaFileMutation,
} from "@services/media";
import { useUpdateSurveyFlagsMutation } from "@services/survey-core";
import { updateCurrentSurvey } from "@survey/surveySlice";
import { type ChangeEvent, useEffect, useRef, useState } from "react";

const ClientLogoSection = () => {
  const { survey, dispatch, updateSurvey } = useSurveyState();
  const [updateSurveyFlags] = useUpdateSurveyFlagsMutation();
  const [uploadOwnerMediaFile, { isLoading: isUploading }] =
    useUploadOwnerMediaFileMutation();
  const [deleteOwnerMediaFile, { isLoading: isDeleting }] =
    useDeleteOwnerMediaFileMutation();

  const [showError, setShowError] = useState(false);

  const inputRef = useRef<HTMLInputElement | null>(null);

  const {
    data: clientLogoResponse,
    isSuccess: isClientLogoSuccess,
    refetch: refetchClientLogoMedia,
  } = useGetSurveyMediaFileQuery(
    { mediaType: "CLIENTLOGO", surveyId: survey?.id },
    { skip: !survey?.id },
  );

  // Update Redux store when client logo data is available
  useEffect(() => {
    if (isClientLogoSuccess && clientLogoResponse?.media?.[0]?.cloudfrontUrl) {
      const clientLogo = clientLogoResponse.media[0].cloudfrontUrl;

      // Update Redux store if clientLogo is different
      if (clientLogo !== survey?.clientLogo) {
        dispatch(updateCurrentSurvey({ clientLogo }));
      }
    }
  }, [isClientLogoSuccess, clientLogoResponse, survey?.clientLogo, dispatch]);

  const logoMedia = clientLogoResponse?.media || [];

  const handleCheckboxChange = (propertyName: string) => (checked: boolean) => {
    if (survey?.id) {
      updateSurveyFlags({ id: survey.id, [propertyName]: checked });
    }
  };

  // Logo upload handler
  const handleLogoUpload = (event: ChangeEvent<HTMLInputElement>) => {
    const fileObj = event.target.files?.[0];
    if (!fileObj || !survey?.id) return;

    // Reset input value
    event.target.value = "";

    const img = new Image();
    img.setAttribute("src", URL.createObjectURL(fileObj));

    img.onload = async () => {
      // Clean up object URL
      URL.revokeObjectURL(img.src);

      if (img.width >= 10000 || img.height >= 10000) {
        setShowError(true);
        return;
      }

      try {
        await mediaUpload(
          fileObj,
          survey.id,
          "CLIENTLOGO",
          uploadOwnerMediaFile,
        );

        // Refetch to get the new logo URL
        const updatedResponse = await refetchClientLogoMedia();
        const newClientLogo = updatedResponse.data?.media?.[0]?.cloudfrontUrl;

        if (newClientLogo) {
          const updates = {
            clientLogo: newClientLogo,
            ...(!survey.isDisplayClientLogo && { isDisplayClientLogo: true }),
          };

          await updateSurvey({ id: survey.id, ...updates });
        }
      } catch (error) {
        console.error("Failed to upload client logo");
        setShowError(true);
      }
    };

    img.onerror = () => {
      URL.revokeObjectURL(img.src);
      setShowError(true);
    };
  };

  // Logo removal handler
  const handleLogoRemove = async () => {
    if (!survey?.id || !logoMedia[0]) return;

    const payloadDelete = {
      mediaId: logoMedia[0].mediaId,
      mediaType: "CLIENTLOGO" as const,
      surveyId: survey.id,
      propertyId: "",
      ownerType: "PROPERTY" as const,
    };

    try {
      const response = await deleteOwnerMediaFile(payloadDelete).unwrap();
      refetchClientLogoMedia();

      if (!response.media?.length) {
        await updateSurvey({ id: survey.id, clientLogo: "" });
      }
    } catch (error) {
      console.error("Failed to remove client logo");
    }
  };

  // File input click handler
  const handleFileInputClick = () => {
    inputRef.current?.click();
  };

  // Close error banner
  const closeErrorBanner = () => {
    setShowError(false);
  };

  return (
    <section className="flex flex-col gap-y-4 w-full border-t border-solid border-divider-3 pt-4">
      <div className="mb-1 text-base">Client logo</div>

      {showError && (
        <EmeraldNotification
          variant="error"
          message="Site IQ supports JPG, PNG & AVIF images that are between 550px and 10,000px.  Please re-upload a different file."
          closable
          onClose={closeErrorBanner}
          className="w-full!"
        />
      )}

      <input
        ref={inputRef}
        type="file"
        accept=".jpg,.jpeg,.png,.avif,.av1"
        style={{ display: "none" }}
        onChange={handleLogoUpload}
      />

      {logoMedia.length > 0 ? (
        <>
          <div className="flex items-center justify-between">
            <div className="break-all">
              {logoMedia[0]?.displayName || "Client Logo"}
            </div>
            {logoMedia[0]?.origin !== "SiteIQ" && (
              <>
                {isDeleting ? (
                  <div className="w-8 h-8 flex items-center justify-center">
                    <EmeraldProgressSpinner
                      title=""
                      size="small"
                      isWhite={false}
                      determinate={false}
                    />
                  </div>
                ) : (
                  <EmeraldIconButton
                    icon="clear"
                    type="Icon"
                    onClick={handleLogoRemove}
                  />
                )}
              </>
            )}
          </div>
          <div className="flex items-center gap-2">
            <EmeraldButton
              id="SC-ChangeClientLogo"
              icon="add"
              size="Small"
              label="Change Client Logo"
              type="TextButton"
              disabled={isUploading}
              iconPosition="Left"
              onClick={handleFileInputClick}
            />
            {isUploading && (
              <EmeraldProgressSpinner
                title=""
                size="small"
                isWhite={false}
                determinate={false}
              />
            )}
          </div>
          <div className="flex flex-col">
            <EmeraldCheckbox
              id="displayClientLogo SC-ClientLogoShowHide"
              checked={survey?.isDisplayClientLogo ?? false}
              label="Display Client Logo"
              onChange={handleCheckboxChange("isDisplayClientLogo")}
            />
            <EmeraldCheckbox
              id="transparentLogo SC-ClientLogoTransparent"
              checked={survey?.isClientLogoTransparent ?? false}
              label="Remove White Logo Background"
              onChange={handleCheckboxChange("isClientLogoTransparent")}
            />
            <EmeraldCheckbox
              id="unrestrictedLogoSize SC-UnrestrictedLogoSize"
              checked={survey?.unrestrictedLogoSize ?? false}
              label="Unrestricted Logo Size"
              onChange={handleCheckboxChange("unrestrictedLogoSize")}
            />
          </div>
        </>
      ) : (
        <div className="flex items-center gap-2">
          <EmeraldButton
            id="SC-AddClientLogo"
            icon="add"
            size="Small"
            label="Add Client Logo"
            type="TextButton"
            disabled={isUploading}
            onClick={handleFileInputClick}
          />
          {isUploading && (
            <EmeraldProgressSpinner
              title=""
              size="small"
              isWhite={false}
              determinate={false}
            />
          )}
        </div>
      )}
    </section>
  );
};

// Add displayName for better debugging
ClientLogoSection.displayName = "ClientLogoSection";

export default ClientLogoSection;
